'use client';
import { gsap } from 'gsap';
import { useRef, useEffect, useState } from 'react';
import useVideoPlayback from '@repo/ui/src/hooks/useVideoPlayback';
import dynamic from 'next/dynamic';
import Image from '@repo/ui/src/components/Image/New';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ToutContentButtons } from '@repo/ui/src/components/ToutContent';
import { ImageProps, MediaType, HeaderVariationProps } from '@repo/ui/src/types';
import { AlignH } from '@repo/ui/src/types/general';
import { AmplienceButtonProps } from '@repo/ui/src/types/button';
import { useMounted } from '@repo/ui/src/hooks/useMounted';
import { useIsMobile, useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { classNames } from '@repo/ui/src/utils/classNames';
import PauseIcon from '@repo/ui/src/components/Icons/PauseIcon';
import PlayIcon from '@repo/ui/src/components/Icons/PlayIcon';
import { appConfig } from '../../utils/appConfig';

const BackgroundVideo = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'), {
  ssr: false,
});

interface HomeHeroProps {
  lilHeading: string;
  headingLine1: string;
  headingLine2: string;
  headingLine3: string;
  subheader?: string;
  headerVariation?: HeaderVariationProps;
  image?: ImageProps;
  buttons: Array<AmplienceButtonProps>;
  align: AlignH;
  backgroundImage: MediaType;
  backgroundMedia?: MediaType;
  _testID?: string;
}

const HomeHero = ({
  lilHeading,
  headingLine1,
  headingLine2,
  headingLine3,
  subheader,
  headerVariation,
  image,
  buttons,
  align,
  backgroundImage,
  backgroundMedia,
}: HomeHeroProps) => {
  const fullSize = !!(headingLine1 && image?.image && buttons?.length);
  const ref = useRef<any>(null);
  const { play, pause } = useVideoPlayback(
    () => ref.current?.querySelector('video') as HTMLVideoElement | null,
  );
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const imageUrl = buildImageSrc(image, 500, 0, 0);
  const reduceMotion = useReducedMotion();
  const isMounted = useMounted();
  const xsOnly = useIsMobile();

  const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';

  const activeMedia = backgroundMedia || backgroundImage;
  const isVideo = activeMedia?._meta?.schema === VIDEO_META_SCHEMA;
  const isImage = !isVideo;

  useEffect(() => {
    if (!isMounted) return;

    if (reduceMotion) {
      setIsVideoPlaying(false);
    }

    const element = ref?.current;
    const content = element?.querySelector('.hero-banner-content');
    const fade = element?.querySelector('.fade-container');
    const timeline = gsap.timeline();
    timeline
      .to(fade, {
        delay: 0.2,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
      })
      .fromTo(
        content,
        { translateY: reduceMotion ? '0%' : '7%' },
        { translateY: '0%', opacity: 1, duration: 1, ease: 'power3.out' },
      );
  }, [isMounted, reduceMotion]);

  const width = xsOnly ? 600 : 2000;
  let aspectRatioWidth: number, aspectRatioHeight: number;
  if (xsOnly && fullSize) {
    aspectRatioWidth = 9;
    aspectRatioHeight = 16;
  } else {
    aspectRatioWidth = 16;
    aspectRatioHeight = 9;
  }

  const backgroundImageUrl =
    isImage && activeMedia
      ? buildImageSrc(activeMedia, width, aspectRatioWidth, aspectRatioHeight)
      : null;

  const videoWidth = width > 1920 ? 1920 : width;

  const togglePlayVideo = () => {
    const element = ref.current;
    if (element) {
      const videoElement = element.querySelector('video');
      if (videoElement) {
        if (isVideoPlaying) {
          pause();
          setIsVideoPlaying(false);
        } else {
          play();
          setIsVideoPlaying(true);
        }
      }
    }
  };

  const getAlignment = (align: AlignH) => {
    if (align === 'left') return 'items-start text-left';
    if (align === 'right') return 'items-end text-right';
    return 'items-center text-center';
  };
  const alignment = getAlignment(align);

  const HeadingElement = headerVariation?.headerVariation || 'h2';

  return (
    <section
      ref={ref}
      className={classNames(
        'relative overflow-hidden w-full min-h-[75vh]',
        isImage && 'bg-cover bg-center',
        fullSize && 'h-screen',
      )}
      style={isImage ? { backgroundImage: `url(${backgroundImageUrl})` } : {}}
    >
      {isVideo && activeMedia && (
        <div className="absolute inset-0 z-0">
          <BackgroundVideo
            video={activeMedia}
            width={videoWidth}
            fillContainer
            className="w-full h-full object-cover"
            videoOverlay={false}
          />
        </div>
      )}

      <div className="fade-container absolute inset-0 bg-black opacity-100 z-0 min-h-[75vh]"></div>
      <div
        className={classNames(
          'h-full bg-auto z-10 relative min-h-[75vh]',
          align === 'left' && 'bg-gradient-to-l',
          align === 'right' && 'bg-gradient-to-r',
          align === 'left' || align === 'right'
            ? 'to-[rgba(0,0,0,0.5)_18%] from-transparent'
            : 'bg-[radial-gradient(circle,rgba(0,0,0,0.7)_18%,transparent_100%)]',
        )}
      >
        <div
          className={classNames(
            'hero-banner-content opacity-0 flex flex-col justify-center h-full min-h-[75vh] p-[3vw]',
            appConfig.isMeater && 'py-[5vw]',
            alignment,
          )}
        >
          {(headingLine1 || headingLine2 || headingLine3) && (
            <HeadingElement
              className={classNames(
                'tra-text-web-banner relative text-neutral-100',
                !subheader && 'mb-8 md:mb-12',
                appConfig.isTraeger && 'tra-text-web-banner text-[clamp(5rem,3.5rem+6vw,10rem)]',
              )}
            >
              {lilHeading && (
                <span
                  className={classNames(
                    'block text-[30%] leading-[120%]',
                    appConfig.isMeater && 'tra-text-web-heading-4 uppercase',
                  )}
                >
                  {lilHeading}
                </span>
              )}
              <span className="sr-only">&nbsp;</span>
              {headingLine1 && (
                <span
                  className={classNames(
                    'block mt-2',
                    appConfig.isMeater && 'tra-text-web-heading-1',
                  )}
                >
                  {headingLine1}
                </span>
              )}
              <span className="sr-only">&nbsp;</span>
              {headingLine2 && (
                <span
                  className={classNames(
                    'block',
                    appConfig.isMeater && 'tra-text-web-heading-1 mt-2',
                  )}
                >
                  {headingLine2}
                </span>
              )}
              <span className="sr-only">&nbsp;</span>
              <span
                className={classNames('font-bold', appConfig.isMeater && 'tra-text-web-heading-1')}
              >
                {headingLine3}
              </span>
            </HeadingElement>
          )}

          {subheader && (
            <div className="my-8 md:my-12 max-w-[38rem]">
              <hr className="border-2 w-10 border-brand-300 mb-8" />
              <p className="tra-text-web-body-xl text-neutral-100">{subheader}</p>
            </div>
          )}

          {image?.image && (
            <div className="mb-8 relative w-[58vw] md:w-[18vw] portrait:md:w-[36vw]">
              <Image
                src={imageUrl}
                alt={image.image.name || 'Hero logo'}
                width={500}
                height={500}
                className="w-full h-auto"
                priority
              />
            </div>
          )}

          <ToutContentButtons
            buttons={buttons}
            align={align}
            buttonSize={appConfig.isMeater ? 'medium' : 'large'}
            buttonNegative
            dark
          />
        </div>

        {isVideo && !reduceMotion && (
          <button
            className="absolute left-10 bottom-3 z-[1010] md:left-5 hover:text-brand-400"
            aria-label={isVideoPlaying ? 'Pause Video' : 'Play Video'}
            onClick={togglePlayVideo}
          >
            {isVideoPlaying ? <PauseIcon color="white" /> : <PlayIcon color="white" />}
          </button>
        )}
      </div>
    </section>
  );
};

export default HomeHero;
